import type { TFunction } from 'i18next';

import { MIN_EMPLOYEES_FOR_QUOTES } from '../../../shared/constants/quotes';
import type { IQuote } from '../../../shared/interfaces/quotes';
import { TRANSLATIONS_QUOTES } from '../../../shared/translations/quotes';
import {
  addonIsAgeDependent,
  checkCanSave,
  getAverageAgeError,
  getSaveTooltip,
  type IProductGroup,
  type SelectionState
} from '../../../shared/utils/quotes/quotes';

export interface IUseCreateQuoteFlagsParams {
  groups: IProductGroup[];
  selection: SelectionState;
  employees: string;
  employeesNum: number;
  averageAge: string;
  ageValid: boolean;
  quoteName: string;
  saving: boolean;
  isEditing: boolean;
  editingQuote: IQuote | null;
  commercialPlansCount: number;
  hasSelectedBundle: boolean;
  hasMissingRate: boolean;
  /** App only has no addon products to pick — the bundle is the whole offer. */
  requiresProductSelection?: boolean;
  /** App only skips the carrier headcount minimum entirely. */
  minEmployees?: number;
  t: TFunction;
}

export interface IUseCreateQuoteFlagsResult {
  selectedCount: number;
  employerPaidSelectedCount: number;
  voluntarySelectedCount: number;
  hasEmployerPaidGroups: boolean;
  hasVoluntaryGroups: boolean;
  isEditDataMissing: boolean;
  offerEmpty: boolean;
  employeesInvalid: boolean;
  bundleRequired: boolean;
  addonsDisabled: boolean;
  addonsBlockedByPlan: boolean;
  ageRequired: boolean;
  employeesError: string | null;
  averageAgeError: string | null;
  canSave: boolean;
  saveTooltip: string | undefined;
}

export const useCreateQuoteFlags = ({
  groups,
  selection,
  employees,
  employeesNum,
  averageAge,
  ageValid,
  quoteName,
  saving,
  isEditing,
  editingQuote,
  commercialPlansCount,
  hasSelectedBundle,
  hasMissingRate,
  requiresProductSelection = true,
  minEmployees = MIN_EMPLOYEES_FOR_QUOTES,
  t
}: IUseCreateQuoteFlagsParams): IUseCreateQuoteFlagsResult => {
  const selectedCount = groups.filter((group) => selection[group.key]?.selected).length;
  const employerPaidSelectedCount = groups.filter(
    (group) => selection[group.key]?.selected && group.employerPaid
  ).length;
  const voluntarySelectedCount = groups.filter((group) => selection[group.key]?.selected && !group.employerPaid).length;
  const hasEmployerPaidGroups = groups.some((group) => group.employerPaid);
  const hasVoluntaryGroups = groups.some((group) => !group.employerPaid);

  const isEditDataMissing = isEditing && !editingQuote;
  const offerEmpty = commercialPlansCount === 0 && groups.length === 0;

  const employeesInvalid = employeesNum < minEmployees;
  const bundleRequired = commercialPlansCount > 0 && !hasSelectedBundle;
  const addonsDisabled = employeesInvalid || bundleRequired;
  const addonsBlockedByPlan = !employeesInvalid && bundleRequired;

  const ageRequired = groups.some(
    (group) =>
      selection[group.key]?.selected &&
      group.employerPaid &&
      addonIsAgeDependent(group.addons.find((a) => a.id === selection[group.key]?.addonId)) &&
      selection[group.key]?.ageInputMode === 'average'
  );

  const employeesError =
    employees !== '' && employeesNum < minEmployees
      ? t(TRANSLATIONS_QUOTES.create.group.employeesMin, { count: minEmployees })
      : null;
  const averageAgeError = getAverageAgeError(averageAge, ageValid, groups, selection, t);

  const canSave =
    !offerEmpty &&
    checkCanSave({
      employeesNum,
      minEmployees,
      selectedCount,
      requiresProductSelection,
      bundleRequired,
      quoteName,
      ageRequired,
      ageValid,
      hasMissingRate,
      saving,
      isEditDataMissing
    });

  const saveTooltip = offerEmpty
    ? t(TRANSLATIONS_QUOTES.create.tooltips.noProductsAvailable)
    : getSaveTooltip({
        isEditDataMissing,
        employeesNum,
        minEmployees,
        quoteName,
        selectedCount,
        requiresProductSelection,
        bundleRequired,
        ageRequired,
        ageValid,
        t
      });

  return {
    selectedCount,
    employerPaidSelectedCount,
    voluntarySelectedCount,
    hasEmployerPaidGroups,
    hasVoluntaryGroups,
    isEditDataMissing,
    offerEmpty,
    employeesInvalid,
    bundleRequired,
    addonsDisabled,
    addonsBlockedByPlan,
    ageRequired,
    employeesError,
    averageAgeError,
    canSave,
    saveTooltip
  };
};
